import React, { useEffect, useState } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+Bengali:wght@400;600;700&family=Noto+Sans+Bengali:wght@400;500;600;700&display=swap');
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const shine = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

/* ─── Layout ─── */
const PageWrapper = styled.div`
  background: #f0f0f1;
  min-height: 100vh;
  padding: 40px 16px 60px;
  font-family: 'Noto Sans Bengali', sans-serif;
`;

const Container = styled.div`
  max-width: 780px;
  margin: 0 auto;
  animation: ${fadeIn} 0.5s ease-out;
`;

/* ─── Admin-bar accent ─── */
const AdminBar = styled.div`
  background: #1d2327;
  color: #a7aaad;
  font-size: 13px;
  padding: 0 20px;
  height: 32px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: -40px -16px 32px;

  span { color: #72aee6; font-weight: 600; }
`;

/* ─── Success Card ─── */
const SuccessCard = styled.div`
  background: #fff;
  border: 1px solid #c3c4c7;
  border-top: 4px solid #00a32a;
  border-radius: 2px;
  padding: 32px 36px;
  margin-bottom: 20px;
  text-align: center;
  box-shadow: 0 1px 1px rgba(0,0,0,.04);

  @media (max-width: 600px) { padding: 24px 20px; }
`;

const CheckCircle = styled.div`
  width: 72px;
  height: 72px;
  background: #00a32a;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  animation: ${pulse} 2.5s ease-in-out infinite;
  box-shadow: 0 0 0 6px #d8ffd8;

  svg { width: 40px; height: 40px; fill: #fff; }
`;

const SuccessTitle = styled.h1`
  font-family: 'Noto Serif Bengali', serif;
  color: #1d2327;
  font-size: 1.9em;
  font-weight: 700;
  margin: 0 0 12px;

  @media (max-width: 600px) { font-size: 1.4em; }
`;

const SuccessMsg = styled.p`
  color: #50575e;
  font-size: 1em;
  line-height: 1.8;
  margin: 0;
`;

/* ─── WP Meta Box ─── */
const MetaBox = styled.div`
  background: #fff;
  border: 1px solid #c3c4c7;
  border-radius: 2px;
  margin-bottom: 20px;
  box-shadow: 0 1px 1px rgba(0,0,0,.04);
  animation: ${fadeIn} 0.6s ease-out ${props => props.delay || '0s'} both;
`;

const MetaBoxHeader = styled.div`
  background: #f6f7f7;
  border-bottom: 1px solid #c3c4c7;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  h2 {
    font-size: 14px;
    font-weight: 600;
    color: #1d2327;
    margin: 0;
    font-family: 'Noto Sans Bengali', sans-serif;
  }
  span { font-size: 16px; }
`;

const MetaBoxBody = styled.div`
  padding: 20px;

  @media (max-width: 600px) { padding: 16px; }
`;

/* ─── Order Table ─── */
const OrderTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  tr:last-child td { border-bottom: none; }
`;

const OrderRow = styled.tr`
  border-bottom: 1px solid #f0f0f1;

  &:hover { background: #f6f7f7; }

  td {
    padding: 10px 8px;
    color: #50575e;
    vertical-align: top;

    &:first-child {
      width: 42%;
      color: #1d2327;
      font-weight: 600;
    }
    &:last-child { color: #2c3338; }
  }

  @media (max-width: 600px) {
    display: block;
    td {
      display: block;
      padding: 6px 4px;
      width: 100% !important;
      &:first-child { padding-bottom: 2px; font-size: 12px; color: #646970; }
    }
    border-bottom: 1px solid #f0f0f1;
    padding: 6px 0;
  }
`;

const StatusBadge = styled.span`
  background: #edfaef;
  color: #00a32a;
  border: 1px solid #b3e6b3;
  padding: 3px 10px;
  border-radius: 2px;
  font-size: 12px;
  font-weight: 600;
`;

/* ─── Blood Donation Section ─── */
const BloodBox = styled(MetaBox)`
  border-top: 4px solid #d63638;
`;

const BloodGroupGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin: 20px 0 16px;
`;

const BloodGroupButton = styled.button`
  background: ${props => props.selected ? '#d63638' : '#fff'};
  color: ${props => props.selected ? '#fff' : '#d63638'};
  border: 2px solid ${props => props.selected ? '#d63638' : '#d63638'};
  padding: 14px 8px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 1.15em;
  font-weight: 700;
  font-family: 'Noto Sans Bengali', sans-serif;
  transition: all 0.2s ease;
  box-shadow: ${props => props.selected ? '0 2px 8px rgba(214,54,56,.3)' : 'none'};

  &:hover:not(:disabled) {
    background: ${props => props.selected ? '#b32d2e' : '#fdf2f2'};
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(214,54,56,.2);
  }

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const DonorCountBadge = styled.div`
  background: #fdf2f2;
  border: 1px solid #f5c6c6;
  border-radius: 2px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #d63638;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const BloodDescText = styled.p`
  font-size: 14px;
  color: #50575e;
  line-height: 1.8;
  margin: 0 0 4px;
`;

const ThankYouCard = styled.div`
  background: #fdf2f2;
  border: 1px solid #f5c6c6;
  border-radius: 2px;
  padding: 20px;
  margin-top: 12px;
  animation: ${fadeIn} 0.4s ease-out;
`;

const ThankYouCardTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #d63638;
  margin-bottom: 10px;
`;

const ThankYouCardText = styled.p`
  font-size: 14px;
  color: #50575e;
  line-height: 1.8;
  margin: 0 0 8px;
`;

const SaveStatus = styled.div`
  font-size: 13px;
  color: #646970;
  font-style: italic;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border: 2px solid #646970;
    border-top-color: transparent;
    border-radius: 50%;
    display: inline-block;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }
`;

/* ─── Footer Button ─── */
const HomeButton = styled.button`
  background: #2271b1;
  color: #fff;
  border: none;
  padding: 10px 24px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Noto Sans Bengali', sans-serif;
  transition: background 0.2s;
  display: block;
  margin: 0 auto;
  box-shadow: 0 1px 2px rgba(0,0,0,.12);

  &:hover { background: #135e96; }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #c3c4c7;
  margin: 16px 0;
`;

/* ═══════════════════════════════════════════ */

function ThankYouPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const orderSummary = location.state?.orderSummary;

  const [selectedBloodGroup, setSelectedBloodGroup] = useState('');
  const [isSavingBlood, setIsSavingBlood] = useState(false);
  const [totalDonors, setTotalDonors] = useState(5000);

  useEffect(() => {
    if (!orderSummary) {
      navigate('/', { replace: true });
      return;
    }

    let storedOrders = [];
    try {
      const storedData = localStorage.getItem('orderHistory');
      if (storedData) {
        storedOrders = JSON.parse(storedData);
      }
    } catch (error) {
      console.error("localStorage থেকে অর্ডারের ইতিহাস পার্স করতে ত্রুটি:", error);
      storedOrders = [];
    }

    if (Object.keys(orderSummary).length > 0) {
      const simplifiedOrder = {
        orderId: orderSummary.orderId,
        user_id: orderSummary.user_id,
        customerInfo: orderSummary.customerInfo,
        cartItems: orderSummary.cartItems,
        totalAmount: orderSummary.totalAmount,
        paymentMethod: orderSummary.paymentMethod,
        paymentDetails: orderSummary.paymentDetails,
        orderDate: orderSummary.orderDate,
        orderTime: orderSummary.orderTime,
        created_at: new Date().toISOString(),
        CustomerDeliveryCharge: orderSummary.CustomerDeliveryCharge
      };
      storedOrders.push(simplifiedOrder);
    }

    const twentyDaysAgo = new Date();
    twentyDaysAgo.setDate(twentyDaysAgo.getDate() - 20);

    const updatedOrders = storedOrders.filter(order => {
      const createdAtDate = new Date(order.created_at);
      return createdAtDate >= twentyDaysAgo;
    });

    try {
      localStorage.setItem('orderHistory', JSON.stringify(updatedOrders));
    } catch (error) {
      console.error("localStorage এ অর্ডারের ইতিহাস সেভ করতে ত্রুটি:", error);
    }
  }, [orderSummary, navigate]);

  useEffect(() => {
    fetch('https://aman-life-style-bd-main-server.sparkx-ai.shop/get_blood_donor_count')
      .then(res => res.json())
      .then(data => {
        if (data.total_donors) {
          setTotalDonors(data.total_donors);
        }
      })
      .catch(err => console.error('Error fetching donor count:', err));
  }, []);

  const handleBloodGroupClick = async (bloodGroup) => {
    setSelectedBloodGroup(bloodGroup);
    setIsSavingBlood(true);

    try {
      const response = await fetch('https://aman-life-style-bd-main-server.sparkx-ai.shop/save_blood_group', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: orderSummary.customerInfo.phone,
          name: orderSummary.customerInfo.name,
          bloodGroup: bloodGroup,
          location: orderSummary.customerInfo.address,
        }),
      });

      if (!response.ok) {
        alert('তথ্য সংরক্ষণে সমস্যা হয়েছে। আবার চেষ্টা করুন।');
        setSelectedBloodGroup('');
      }
    } catch (error) {
      console.error('Blood donation save error:', error);
      alert('তথ্য সংরক্ষণে সমস্যা হয়েছে।');
      setSelectedBloodGroup('');
    } finally {
      setIsSavingBlood(false);
    }
  };

  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];

  if (!orderSummary) return null;

  const paymentLabel =
    orderSummary.paymentMethod === 'cashOnDelivery' ? 'ক্যাশ অন ডেলিভারি' :
      orderSummary.paymentMethod === 'bkash' ? 'বিকাশ' : 'নগদ';

  return (
    <>
      <GlobalStyle />
      <PageWrapper>
        <AdminBar>
          <span>✓</span> অর্ডার সফলভাবে গৃহীত হয়েছে
        </AdminBar>

        <Container>
          {/* ── Success Card ── */}
          <SuccessCard>
            <CheckCircle>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-111 111-47-47c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l64 64c9.4 9.4 24.6 9.4 33.9 0L369 209z" />
              </svg>
            </CheckCircle>
            <SuccessTitle>🎉 অভিনন্দন! আপনার অর্ডার সফল হয়েছে</SuccessTitle>
            <SuccessMsg>
              আপনার অর্ডারটি সফলভাবে গৃহীত হয়েছে। আমরা খুব শীঘ্রই আপনার সাথে যোগাযোগ করব এবং পণ্য ডেলিভারি করব।
            </SuccessMsg>
          </SuccessCard>

          {/* ── Order Details Meta Box ── */}
          <MetaBox delay="0.15s">
            <MetaBoxHeader>
              <span>📋</span>
              <h2>অর্ডারের বিস্তারিত</h2>
            </MetaBoxHeader>
            <MetaBoxBody style={{ padding: '0' }}>
              <OrderTable>
                <tbody>
                  <OrderRow>
                    <td>📅 অর্ডার তারিখ</td>
                    <td>{orderSummary.orderDate}</td>
                  </OrderRow>
                  <OrderRow>
                    <td>🕐 অর্ডার সময়</td>
                    <td>{orderSummary.orderTime}</td>
                  </OrderRow>
                  <OrderRow>
                    <td>💰 মোট মূল্য</td>
                    <td><strong>৳{orderSummary.totalAmount + orderSummary.CustomerDeliveryCharge}</strong></td>
                  </OrderRow>
                  <OrderRow>
                    <td>💳 পেমেন্ট পদ্ধতি</td>
                    <td><StatusBadge>{paymentLabel}</StatusBadge></td>
                  </OrderRow>
                  {orderSummary.paymentDetails && (
                    <>
                      <OrderRow>
                        <td>🔖 TrxID</td>
                        <td>{orderSummary.paymentDetails.trxId}</td>
                      </OrderRow>
                      <OrderRow>
                        <td>📱 পেমেন্ট নম্বর</td>
                        <td>{orderSummary.paymentDetails.phoneNo}</td>
                      </OrderRow>
                    </>
                  )}
                  <OrderRow>
                    <td>👤 গ্রাহকের নাম</td>
                    <td>{orderSummary.customerInfo.name}</td>
                  </OrderRow>
                  <OrderRow>
                    <td>📞 মোবাইল নম্বর</td>
                    <td>{orderSummary.customerInfo.phone}</td>
                  </OrderRow>
                  <OrderRow>
                    <td>📍 ঠিকানা</td>
                    <td>{orderSummary.customerInfo.address}</td>
                  </OrderRow>
                  {orderSummary.customerInfo.notes && (
                    <OrderRow>
                      <td>📝 বিশেষ নোট</td>
                      <td>{orderSummary.customerInfo.notes}</td>
                    </OrderRow>
                  )}
                </tbody>
              </OrderTable>
            </MetaBoxBody>
          </MetaBox>

          {/* ── Blood Donation Meta Box ── */}
          <BloodBox delay="0.3s">
            <MetaBoxHeader>
              <span>🩸</span>
              <h2>রক্তদান করুন, জীবন বাঁচান</h2>
            </MetaBoxHeader>
            <MetaBoxBody>
              <DonorCountBadge>
                🤝 আমাদের সাথে যুক্ত আছেন {(totalDonors + 5000).toLocaleString()} পরিবার
              </DonorCountBadge>

              {!selectedBloodGroup ? (
                <>
                  <BloodDescText>
                    আপনার রক্তের গ্রুপ শেয়ার করুন এবং জরুরি মুহূর্তে আপনার এলাকার মা, বাবা, ভাই, বোন বা প্রিয়জনকে সাহায্য করুন।
                    একটি ছোট্ট পদক্ষেপে আপনি হতে পারেন কারো জীবন রক্ষাকারী।
                    আপনার দেওয়া তথ্য সম্পূর্ণ গোপনীয় থাকবে এবং শুধুমাত্র জরুরি প্রয়োজনে ব্যবহার করা হবে।
                  </BloodDescText>
                  <BloodGroupGrid>
                    {bloodGroups.map(group => (
                      <BloodGroupButton
                        key={group}
                        onClick={() => handleBloodGroupClick(group)}
                        disabled={isSavingBlood}
                      >
                        {group}
                      </BloodGroupButton>
                    ))}
                  </BloodGroupGrid>
                  {isSavingBlood && <SaveStatus>সংরক্ষণ হচ্ছে...</SaveStatus>}
                </>
              ) : (
                <ThankYouCard>
                  <ThankYouCardTitle>🙏 অসংখ্য ধন্যবাদ!</ThankYouCardTitle>
                  <ThankYouCardText>
                    আপনার রক্তের গ্রুপ <strong>{selectedBloodGroup}</strong> সফলভাবে সংরক্ষিত হয়েছে।
                    আপনার এই মহৎ উদ্যোগ জরুরি মুহূর্তে কারো জীবন বাঁচাতে পারে।
                    মানবতার সেবায় আপনার এই অবদান সত্যিই প্রশংসনীয়।
                    প্রয়োজনে আমরা আপনার সাথে যোগাযোগ করব।
                  </ThankYouCardText>
                  <ThankYouCardText style={{ fontSize: '13px', marginTop: '12px', color: '#646970' }}>
                    পরিবর্তন করতে চান? নিচে আপনার নতুন গ্রুপ সিলেক্ট করুন:
                  </ThankYouCardText>
                  <BloodGroupGrid>
                    {bloodGroups.map(group => (
                      <BloodGroupButton
                        key={group}
                        selected={group === selectedBloodGroup}
                        onClick={() => handleBloodGroupClick(group)}
                        disabled={isSavingBlood}
                      >
                        {group}
                      </BloodGroupButton>
                    ))}
                  </BloodGroupGrid>
                  {isSavingBlood && <SaveStatus>আপডেট হচ্ছে...</SaveStatus>}
                </ThankYouCard>
              )}
            </MetaBoxBody>
          </BloodBox>

          <HomeButton onClick={() => navigate('/')}>🏠 হোম পেজে ফিরে যান</HomeButton>
        </Container>
      </PageWrapper>
    </>
  );
}

export default ThankYouPage;